import { useTranslation } from "react-i18next"
import type { Report } from "@/types/report"
import type { Parameter } from "@/types/parameter"
import { ParameterRenderer } from "./parameter-renderer"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { useLocalStorage } from "@/hooks/use-local-storage"
import { GLOBAL_PARAMETER_NAMES } from "@/lib/constants"

interface ParameterPanelProps {
  /** The report whose parameters to display */
  report: Report | null
  /** Callback when a parameter value changes */
  onValueSet?: (parameter: Parameter, report: Report) => void
  /** List of parameter names to exclude from display (defaults to GLOBAL_PARAMETER_NAMES) */
  excludeList?: readonly string[]
}

/**
 * Filters parameters to only "list-defined" parameters.
 * Shows all visible parameters except those in the exclusion list.
 */
function getListDefinedParameters(
  parameters: Parameter[],
  excludeList: readonly string[],
): Parameter[] {
  return parameters.filter(
    (p) =>
      p.isVisible &&
      !excludeList.includes(p.name.toLowerCase()),
  )
}

export function ParameterPanel({
  report,
  onValueSet,
  excludeList = GLOBAL_PARAMETER_NAMES,
}: Readonly<ParameterPanelProps>) {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useLocalStorage("showAllParameters", false)
  const isDevelopment = import.meta.env.DEV

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="font-semibold">{t("parameterPanel.noParameters")}</p>
        <p className="text-sm text-muted-foreground">
          {t("parameterPanel.noParametersHelp")}
        </p>
      </div>
    )
  }

  const parameters = Array.isArray(report.parameters) ? report.parameters : []
  const visibleParameters = showAll
    ? parameters.filter((p) => p.isVisible)
    : getListDefinedParameters(parameters, excludeList)

  const handleValueChange = (parameter: Parameter) => {
    if (onValueSet && report) {
      onValueSet(parameter, report)
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {isDevelopment && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-all-params"
            checked={showAll}
            onCheckedChange={(checked) => setShowAll(checked === true)}
          />
          <Label htmlFor="show-all-params" className="text-sm font-normal">
            {t("parameterPanel.showAllParameters")}
          </Label>
        </div>
      )}
      {visibleParameters.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("parameterPanel.noVisibleParameters")}
        </p>
      ) : (
        <div className="space-y-4">
          {visibleParameters.map((parameter) => (
            <ParameterRenderer
              key={parameter.name}
              parameter={parameter}
              onChange={handleValueChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}
